import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { recordElementClicked, recordScreenViewed } from '@streem/analytics';
import { IncomingCallNotification, LegacyIncomingCallNotification } from '@streem/ui-react';
import { audio } from '../../media';
import { logger } from '../../util/logger';
import { useReservationAudio } from '../../hooks/use_reservation_audio';
import { getPreferredAudioOutputId } from '@streem/toolbox';
import { DeclineCallConfirmation } from './decline_call_confirmation';
export const CallNotification = ({ roomInvitation, onAnswer, onReject, brandSettings, showReservationCallDetailYn = false, }) => {
    const { pauseReservationAudio } = useReservationAudio();
    const [hasExpertClickedAnswerYn, setHasExpertClickedAnswerYn] = useState(false);
    const [isDeclineCallModalDisplaying, setIsDeclineModalDisplaying] = useState(false);
    const disableNotificationButtons = isDeclineCallModalDisplaying || hasExpertClickedAnswerYn;
    const handleOnEnter = useCallback(async () => {
        async function playRingtoneAudio() {
            try {
                await audio.playRingtoneAudio();
            }
            catch (error) {
                logger.warn(`Failed to play incoming call ringtone for invitationId=${roomInvitation.id}, roomId=${roomInvitation.roomId}, from userId=${roomInvitation.fromUserId}, preferredAudioOutputId=${getPreferredAudioOutputId()}`, error);
            }
        }
        logger.info(`Pausing reservation ringtone audio for invitation: ${roomInvitation.id}, room: ${roomInvitation.roomId}, from user: ${roomInvitation.fromUserId}`);
        pauseReservationAudio();
        logger.info(`Playing incoming call ringtone audio for invitation: ${roomInvitation.id}, room: ${roomInvitation.roomId}, from user: ${roomInvitation.fromUserId}`);
        await playRingtoneAudio();
        recordScreenViewed('incoming-call-notification', roomInvitation.roomId);
    }, [
        pauseReservationAudio,
        roomInvitation.fromUserId,
        roomInvitation.id,
        roomInvitation.roomId,
    ]);
    const handleOnExit = useCallback(() => {
        logger.info(`Pausing incoming call ringtone audio for invitation: ${roomInvitation.id}, room: ${roomInvitation.roomId}, from user: ${roomInvitation.fromUserId}`);
        audio.pauseRingtoneAudio();
    }, [roomInvitation.fromUserId, roomInvitation.id, roomInvitation.roomId]);
    const handleOnAnswer = useCallback(async () => {
        setHasExpertClickedAnswerYn(true);
        recordElementClicked('answer-button', roomInvitation.roomId);
        logger.info(`Clicked Answer for invitationId=${roomInvitation.id}, roomId=${roomInvitation.roomId}, from userId=${roomInvitation.fromUserId}`);
        await onAnswer();
    }, [onAnswer, roomInvitation.fromUserId, roomInvitation.id, roomInvitation.roomId]);
    const handleOnDecline = async () => {
        setIsDeclineModalDisplaying(true);
        logger.info('Displaying Decline Call Modal');
    };
    const handleOnDeclineConfirmationSelected = useCallback(async () => {
        recordElementClicked('decline-confirmation-modal-decline-button');
        try {
            setIsDeclineModalDisplaying(false);
            logger.info(`Clicked Reject for invitationId=${roomInvitation.id}, roomId=${roomInvitation.roomId}, from userId=${roomInvitation.fromUserId}`);
            await onReject();
        }
        catch (e) {
            logger.error(`Failed to reject invitationId=${roomInvitation.id}, roomId=${roomInvitation.roomId}, from userId=${roomInvitation.fromUserId}`);
        }
    }, [onReject, roomInvitation.fromUserId, roomInvitation.id, roomInvitation.roomId]);
    const handleOnDeclineConfirmationCancel = useCallback(async () => {
        logger.info(`Clicked cancel for reject confirmation for invitationId=${roomInvitation.id}, roomId=${roomInvitation.roomId}, from userId=${roomInvitation.fromUserId}`);
        recordElementClicked('decline-confirmation-modal-cancel-button');
        setIsDeclineModalDisplaying(false);
    }, [roomInvitation.fromUserId, roomInvitation.id, roomInvitation.roomId]);
    // Handles customer canceling call before expert can answer.
    useEffect(() => {
        return () => {
            logger.info('CallNotification component unmounted');
            // If audio wasn't successfully paused by the onExit or the customer cancelled the call
            // before expert could answer, call the pause ringtone function
            if (!audio.ringtoneAudioPaused) {
                logger.info('Pausing ringtone');
                audio.pauseRingtoneAudio();
            }
        };
    }, []);
    useEffect(() => {
        if (disableNotificationButtons) {
            logger.info(`Call notificatoin buttons have been disabled isDeclineCallModalDisplaying=${isDeclineCallModalDisplaying}, hasExpertClickedAnswerYn=${hasExpertClickedAnswerYn}`);
        }
    }, [disableNotificationButtons, isDeclineCallModalDisplaying, hasExpertClickedAnswerYn]);
    return (_jsxs(_Fragment, { children: [showReservationCallDetailYn ? (_jsx(_Fragment, { children: _jsx(IncomingCallNotification, { open: roomInvitation.status === 'PENDING', shouldDisableButtons: disableNotificationButtons, onEnter: handleOnEnter, onExit: handleOnExit, onAnswer: handleOnAnswer, onDecline: handleOnDecline, customerName: roomInvitation.fromUser.name, brandName: brandSettings?.brandName, brandLogoUrl: brandSettings?.brandLogoUrl, brandPrimaryColor: brandSettings?.customTheme?.primaryColor }) })) : (_jsx(LegacyIncomingCallNotification, { open: roomInvitation.status === 'PENDING', fromUserId: roomInvitation.fromUserId.toString(), fromUserName: roomInvitation.fromUser.name || 'user', referenceId: roomInvitation.roomId.toString(), onEnter: handleOnEnter, onExit: handleOnExit, onAnswer: handleOnAnswer, onReject: handleOnDecline, shouldDisableButtons: disableNotificationButtons })), _jsx(DeclineCallConfirmation, { isOpen: isDeclineCallModalDisplaying, onCancel: handleOnDeclineConfirmationCancel, handleDeclineCall: handleOnDeclineConfirmationSelected })] }));
};
//# sourceMappingURL=call_notification.js.map